import React from 'react';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import Avatar from '@mui/material/Avatar';
import Badge from '@mui/material/Badge';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import PersonIcon from '@mui/icons-material/Person';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import Tooltip from '@mui/material/Tooltip';
import { useSession } from '../../context/SessionContext';
import { getColorFromString } from '../../utils/colorUtils';

// Import the new API function
import { removeParticipant as removeParticipantApi } from '../../utils/api';

const StyledBadge = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    backgroundColor: '#44b700',
    color: '#44b700',
    boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
    '&::after': {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      borderRadius: '50%',
      animation: 'ripple 1.2s infinite ease-in-out',
      border: '1px solid currentColor',
      content: '""',
    },
  },
  '@keyframes ripple': {
    '0%': {
      transform: 'scale(.8)',
      opacity: 1,
    },
    '100%': {
      transform: 'scale(2.4)',
      opacity: 0,
    },
  },
}));

const ParticipantList = () => {
  const { participants, participant: currentParticipant, activeItem } = useSession();
  
  // DISABLED: Always log the current participants for debugging - causes too much log noise
  // console.log('ParticipantList rendering with participants:', participants);
  
  // Filter out stale connections (participants who disconnected more than 5 minutes ago)
  // const activeParticipants = participants.filter(p => 
  //   p.isConnected 
  //   // || !p.lastActivity || 
  //   // (new Date() - new Date(p.lastActivity)) < 5 * 60 * 1000
  // );
  const activeParticipants = participants; // Display all participants for debugging
  
  // DISABLED: This useEffect was causing excessive re-renders
  // useEffect(() => {
  //   console.log('ParticipantList updated with:', activeParticipants.length, 'participants');
  // }, [activeParticipants]);
  
  if (!activeParticipants || activeParticipants.length === 0) {
    return (
      <Paper elevation={3} sx={{ p: 2, mb: 3, bgcolor: 'rgba(245, 158, 11, 0.1)', border: '1px solid rgba(245, 158, 11, 0.3)' }}>
        <Typography variant="body1" align="center" fontWeight="bold" color="text.primary">
          No participants yet.
        </Typography>
        <Typography variant="body2" align="center" color="text.secondary" sx={{ mt: 1 }}>
          Share the invite link to get people to join.
        </Typography>
      </Paper>
    );
  }
  
  // Get counts by role and connection status
  const facilitators = activeParticipants.filter(p => p.role === 'facilitator').length;
  const voters = activeParticipants.filter(p => p.role === 'participant').length;
  const observers = activeParticipants.filter(p => p.role === 'observer').length;
  const connected = activeParticipants.filter(p => p.isConnected === true).length;
  const total = activeParticipants.length;

  // Sort participants: facilitators first, then connected participants first
  const sortedParticipants = [...activeParticipants].sort((a, b) => {
    // First sort by role (facilitator > participant > observer)
    if (a.role === 'facilitator' && b.role !== 'facilitator') return -1;
    if (a.role !== 'facilitator' && b.role === 'facilitator') return 1;
    
    // Then by connection status
    if (a.isConnected && !b.isConnected) return -1;
    if (!a.isConnected && b.isConnected) return 1;
    
    // Then alphabetically by name, ensuring names are strings
    const nameA = String(a.name || '');
    const nameB = String(b.name || '');
    return nameA.localeCompare(nameB);
  });

  const handleRemoveParticipant = async (participantIdToRemove) => {
    if (!activeItem) {
      console.error("Session ID is missing, cannot remove participant.");
      // Optionally, show an error to the user (e.g., using a snackbar)
      return;
    }
    console.log(`Attempting to remove participant: ${participantIdToRemove} from session: ${activeItem}`);
    try {
      await removeParticipantApi(participantIdToRemove, activeItem);
      console.log(`Participant ${participantIdToRemove} removed successfully from session ${activeItem}. UI will update via socket event.`);
      // Optimistic update could be done here, but we'll rely on the socket event 'participant-removed'
      // or 'participants-updated' which should be handled in SessionContext.js
    } catch (error) {
      console.error("Failed to remove participant:", error.response?.data?.msg || error.message);
      // Handle error (e.g., show a snackbar to the user)
      // Example: setSnackbar({ open: true, message: `Error: ${error.response?.data?.msg || error.message}`, severity: 'error' });
    }
  };

  return (
    <Paper elevation={3} sx={{ mb: 3, borderTop: connected > 0 ? '3px solid #22c55e' : '3px solid #f59e0b', bgcolor: '#1a1a1a' }}>
      <Box sx={{ p: 2, borderBottom: '1px solid #2a2a2a', bgcolor: connected > 0 ? 'rgba(34, 197, 94, 0.1)' : 'rgba(245, 158, 11, 0.1)' }}>
        <Typography variant="h6" gutterBottom>
          Participants ({connected} of {total} online)
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {`${facilitators} facilitator${facilitators !== 1 ? 's' : ''} • ${voters} voter${voters !== 1 ? 's' : ''} • ${observers} observer${observers !== 1 ? 's' : ''}`}
        </Typography>
      </Box>
      
      <List sx={{
        width: '100%',
        maxHeight: 300,
        overflow: 'auto',
        bgcolor: '#1a1a1a'
      }}>
        {sortedParticipants.map((participant) => {
          const participantId = participant.id || participant._id;
          
          let Icon = PersonIcon;
          if (participant.role === 'facilitator') {
            Icon = SupervisorAccountIcon;
          } else if (participant.role === 'observer') {
            Icon = VisibilityIcon;
          }

          const canRemove = participant.role !== 'facilitator' && participantId !== currentParticipant;

          return (
            <ListItem
              key={participantId}
              sx={{
                opacity: participant.isConnected ? 1 : 0.5,
              }}
              secondaryAction={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {participant.hasVoted && (
                    <CheckCircleIcon color="success" sx={{ mr: canRemove ? 1 : 0 }} />
                  )}
                  {canRemove && (
                    <Tooltip title="Remove participant">
                      <IconButton 
                        edge="end" 
                        aria-label="delete participant"
                        onClick={() => handleRemoveParticipant(participantId)}
                        size="small"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              }
            >
              <ListItemAvatar>
                <Tooltip title={participant.isConnected ? 'Online' : 'Offline'} placement="top">
                  <StyledBadge
                    overlap="circular"
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    variant="dot"
                    invisible={!participant.isConnected}
                  >
                    <Avatar sx={{ bgcolor: getColorFromString(participant.id || participant._id || participant.name) }}>
                      {participant.name ? participant.name.substring(0, 1).toUpperCase() : <Icon />}
                    </Avatar>
                  </StyledBadge>
                </Tooltip>
              </ListItemAvatar>
              <ListItemText
                primary={participant.name}
                secondary={
                  participant.role === 'facilitator'
                    ? 'Facilitator'
                    : participant.role === 'observer'
                    ? 'Observer'
                    : 'Participant'
                }
              />
            </ListItem>
          );
        })}
      </List>
    </Paper>
  );
};

export default ParticipantList; 